import React, { useState, useEffect, useRef, useCallback } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useAuth } from "react-oidc-context";
import MessageList from './components/MessageList';
import MessageInput from './components/MessageInput';
import ThreadHistoryPanel from './components/ThreadHistoryPanel';
import './App.css';
import './landing.css';
import { initGA, trackPageView, trackEvent } from './utils/googleAnalytics';

const TIMEOUT_RECOVERY_TEXT = "Comunicazione interrotta (Timeout). In attesa di possibile recupero...";
const INITIAL_AI_MESSAGE = 'Ciao! Sono il tuo Mobility Manager Assistant. Come posso aiutarti?';
const NEW_CHAT_AI_MESSAGE = 'Ok, iniziamo una nuova conversazione! Come posso aiutarti ora?';

// Funzione Helper per pulire lo storage OIDC
const cleanupOidcStorage = () => {
  console.log("Pulizia storage OIDC in corso...");
  let cleaned = false;
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('oidc.')) {
      console.log(`Rimozione chiave localStorage: ${key}`);
      localStorage.removeItem(key);
      cleaned = true;
    }
  });
  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith('oidc.')) {
      console.log(`Rimozione chiave sessionStorage: ${key}`);
      sessionStorage.removeItem(key);
      cleaned = true;
    }
  });

  const url = new URL(window.location.href);
  let urlChanged = false;

  if (url.hash && (url.hash.includes('code=') || url.hash.includes('state=') || url.hash.includes('error='))) {
      const hashParams = new URLSearchParams(url.hash.substring(1));
      hashParams.delete('code');
      hashParams.delete('state');
      hashParams.delete('error');
      hashParams.delete('error_description');
      hashParams.delete('error_uri');
      const newHash = hashParams.toString() ? `#${hashParams.toString()}` : '';
      window.history.replaceState({}, document.title, url.pathname + url.search + newHash);
      urlChanged = true;
      url.hash = newHash;
  }

   if (url.search && (url.search.includes('code=') || url.search.includes('state=') || url.search.includes('error='))) {
       const searchParams = new URLSearchParams(url.search);
       searchParams.delete('code');
       searchParams.delete('state');
       searchParams.delete('error');
       searchParams.delete('error_description');
       searchParams.delete('error_uri');
       const newSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';
       window.history.replaceState({}, document.title, url.pathname + newSearch + url.hash);
       urlChanged = true;
   }

  if(cleaned || urlChanged) {
    console.log("Storage OIDC e/o URL ripuliti.");
  }
  return cleaned || urlChanged;
};


function App() {
  const auth = useAuth(); // Hook OIDC

  // Stati per i messaggi e la chat
  const [messages, setMessages] = useState([
    { id: Date.now() + Math.random(), text: INITIAL_AI_MESSAGE, sender: 'ai', isLoading: false, isError: false }
  ]);
  const [threadId, setThreadId] = useState(null); // ID del thread OpenAI attivo
  const inputRef = useRef(null); // Riferimento all'input field
  const currentAiMessageId = useRef(null); // ID del messaggio AI a cui si stanno aggiungendo i delta
  const [showSuggestions, setShowSuggestions] = useState(true); // Mostra/nascondi suggerimenti
  const [isTokenExpired, setIsTokenExpired] = useState(false); // Stato per token scaduto
  const [isStartingNewChat, setIsStartingNewChat] = useState(false); // Flag per quando si avvia una nuova chat
  const [isChatLoading, setIsChatLoading] = useState(true); // Caricamento messaggi/cronologia del thread attivo

  // Stati per la cronologia dei thread (lista di thread)
  const [threadHistoryList, setThreadHistoryList] = useState([]); // Lista dei thread dell'utente
  const [isThreadHistoryPanelOpen, setIsThreadHistoryPanelOpen] = useState(false); // Visibilità pannello cronologia
  const [isLoadingThreadHistoryList, setIsLoadingThreadHistoryList] = useState(false); // Caricamento lista cronologia
  const [hasRequestedInitialHistory, setHasRequestedInitialHistory] = useState(false); // Flag per richiesta iniziale cronologia
  
  // Ref per tracciare l'ID del thread appena creato, prima che lo stato sia aggiornato
  const pendingNewThreadId = useRef(null);
  const newThreadJustCreatedAndMessaged = useRef(false); // Per aggiornare la cronologia dopo il primo messaggio di un nuovo thread
  const SQUARE_BRACKET_CITATION_REGEX = /\u3010[^\u3011]*?\u3011/g; // 'g' per global, rimuove tutte le occorrenze

  // --- NUOVI STATI/REF PER GLI ANALYTICS ---
  const userMessageTimestamp = useRef(null); // Per calcolare la latenza della risposta AI
  const lastUserMessageId = useRef(null);    // Per associare la risposta AI alla domanda utente
  const lastUserMessageText = useRef(null);  // Per loggare la domanda utente (PII WARNING!)
  const lastAiMessageText = useRef(null);    // Per accumulare e loggare la risposta AI (PII WARNING!)


  const predefinedSuggestions = [
    "Quali sono i vantaggi del carpooling aziendale?",
    "Come posso organizzare un piano di spostamento casa-lavoro?",
    "Quali incentivi esistono per la mobilità sostenibile?",
    "Parlami delle opzioni di trasporto pubblico nella mia zona."
  ];

  // URL del WebSocket - Assicurati che sia quello corretto
  const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL || 'wss://2h1ovg69sb.execute-api.eu-west-1.amazonaws.com/production/';


  // Configurazione URL WebSocket (dipende da stato autenticazione)
  const wsConnectionURL = auth.isAuthenticated && auth.user?.access_token && !isTokenExpired
    ? WEBSOCKET_URL
    : null;

  // Hook per la connessione WebSocket
  const { sendMessage, lastMessage, readyState, getWebSocket } = useWebSocket(wsConnectionURL, {
    shouldReconnect: (closeEvent) => !!wsConnectionURL,
    reconnectInterval: 3000,
    reconnectAttempts: 10,
    onOpen: () => {
      console.log('FRONTEND: WebSocket Connesso');
      setIsChatLoading(true); 
      setHasRequestedInitialHistory(false); 

      if (auth.isAuthenticated && auth.user?.access_token) {
          const currentKnownThreadId = pendingNewThreadId.current || threadId || auth.user?.profile?.['custom:threadId'];
          console.log("FRONTEND ONOPEN: Invio 'authenticate' con threadId:", currentKnownThreadId, "(pending:", pendingNewThreadId.current, "state:", threadId, "cognito:", auth.user?.profile?.['custom:threadId'], ")");
          const authPayload = {
              action: 'authenticate',
              token: auth.user.access_token, 
              userInfo: {
                  email: auth.user?.profile?.email,
                  sub: auth.user?.profile?.sub,
                  threadId: currentKnownThreadId 
              }
          };
          sendMessage(JSON.stringify(authPayload));
          // Non tracciamo websocket_connected come richiesto.
      } else {
          setIsChatLoading(false);
      }
    },
    onClose: (event) => {
        console.log('FRONTEND: WebSocket Disconnesso', event);
        setIsChatLoading(false);
        // Non tracciamo websocket_disconnected come richiesto.
        if (event.code === 1008 || (event.code >= 4000 && event.code <= 4999) || event.reason?.toLowerCase().includes('unauthorized') || event.reason?.toLowerCase().includes('token')) {
            console.warn("WebSocket chiuso per problemi di autorizzazione/token.");
            setIsTokenExpired(true);
            // --- ANALYTICS: Token scaduto via WS close ---
            trackEvent('auth_token_expired_ws_close', { user_id: auth.user?.profile?.sub });
        }
    },
    onError: (event) => {
        console.error('FRONTEND: Errore WebSocket:', event);
        setIsChatLoading(false);
        // Non tracciamo websocket_error come richiesto.
    },
    retryOnError: !!wsConnectionURL,
  });

  // --- ANALYTICS: Inizializzazione GA e tracciamento page view iniziale ---
  useEffect(() => {
    console.log('GA useEffect in App.js è stato eseguito.');
    initGA();
    trackPageView(window.location.pathname, document.title);
  }, []); // Esegue solo al mount dell'App

  // Parallax animation effects for landing page
  useEffect(() => {
    const circles = document.querySelector("#circles");
    const sparkling = document.querySelector("#sparkling");
    const sparklingLight = document.querySelector("#sparkling_light");

    const setTranslate = (xPos, yPos, el) => {
      if (el) {
        el.style.transform = `translate3d(${xPos}px, ${yPos}px, 0)`;
      }
    };

    const scrollLoop = () => {
      const yScrollPosition = window.scrollY;
      setTranslate(0, yScrollPosition * -0.2, circles);
      setTranslate(0, yScrollPosition * -1.5, sparkling);
      setTranslate(0, yScrollPosition * 0.5, sparklingLight);
      requestAnimationFrame(scrollLoop);
    };

    if (!auth.isAuthenticated) {
      scrollLoop();
    }

    return () => {
      // Cleanup if needed
    };
  }, [auth.isAuthenticated]);


  // Effetto per errori OIDC e recupero threadId iniziale dal profilo utente
  useEffect(() => {
    if (auth.error) {
      console.error("Errore di autenticazione OIDC rilevato:", auth.error);
      // --- ANALYTICS: Errore di autenticazione OIDC ---
      trackEvent('auth_oidc_error', {
          error_message: auth.error.message,
          error_name: auth.error.name
      });
      if (auth.error.message === "No matching state found in storage" ||
          auth.error.message.includes("state") ||
          auth.error.name === "ErrorTimeout") {
        console.log("Rilevato errore di stato OIDC. Pulizia storage e URL.");
        cleanupOidcStorage();
      }
      setIsChatLoading(false); 
    }

    if (auth.isAuthenticated && auth.user?.profile) {
        // --- ANALYTICS: Imposta User ID globale per GA ---
        if (window.gtag) {
            console.log(`GA: Impostazione user_id per GA: ${auth.user.profile.sub}`);
            window.gtag('set', 'user_properties', {
                user_id: auth.user.profile.sub,
                user_email: auth.user.profile.email // Utile come proprietà utente, ma attenzione PII
            });
        }

        const userThreadId = auth.user.profile['custom:threadId'];
        if (userThreadId && userThreadId !== threadId && !isStartingNewChat && !pendingNewThreadId.current) {
            console.log(`useEffect[auth.profile]: Recuperato threadId '${userThreadId}' da custom attribute Cognito. State threadId: ${threadId}, pendingNewThreadId: ${pendingNewThreadId.current}`);
            setThreadId(userThreadId);
        } else if (!userThreadId && !isStartingNewChat && !pendingNewThreadId.current) {
             setIsChatLoading(false); 
        }
    } else if (!auth.isAuthenticated) {
        // --- ANALYTICS: Reset User ID se non autenticato (per sessioni anonime) ---
        if (window.gtag) {
            console.log("GA: Reset user_id per GA (utente non autenticato).");
            window.gtag('set', 'user_properties', { user_id: null, user_email: null });
        }
        setIsChatLoading(false); 
    }
  }, [auth.error, auth.isAuthenticated, auth.user?.profile, threadId, isStartingNewChat]);


  // Effetto per gestione eventi OIDC (scadenza token, caricamento utente, logout)
  useEffect(() => {
    const checkTokenExpiration = () => {
        if (auth.isAuthenticated && auth.user?.expired) {
            console.warn("Token OIDC scaduto (controllo iniziale).");
            setIsTokenExpired(true);
            // --- ANALYTICS: Token scaduto (controllo iniziale) ---
            trackEvent('auth_token_expired_initial_check', { user_id: auth.user?.profile?.sub });
        } else if (auth.isAuthenticated && !auth.user?.expired) {
            setIsTokenExpired(false);
        }
    };
    checkTokenExpiration(); 

    const handleAccessTokenExpired = () => {
        console.warn("Evento OIDC: Access token expired");
        setIsTokenExpired(true);
        // --- ANALYTICS: Token scaduto (evento OIDC) ---
        trackEvent('auth_token_expired_event', { user_id: auth.user?.profile?.sub });
    };

    const handleUserLoaded = (user) => {
        console.log("Evento OIDC: User loaded/refreshed");
        setIsTokenExpired(user.expired ?? false);
        setHasRequestedInitialHistory(false); 
        const userThreadId = user.profile?.['custom:threadId'];
        if (userThreadId && !isStartingNewChat && !pendingNewThreadId.current) {
            console.log(`UserLoaded: Trovato threadId '${userThreadId}'. State threadId: ${threadId}, pendingNewThreadId: ${pendingNewThreadId.current}`);
            setThreadId(userThreadId);
        } else if (!userThreadId && !isStartingNewChat && !pendingNewThreadId.current) {
            console.log("UserLoaded: Nessun custom:threadId nel profilo.");
            setThreadId(null);
            setIsChatLoading(false);
        }
        // --- ANALYTICS: Utente loggato/caricato ---
        trackEvent('user_logged_in', {
            user_email: user.profile?.email,
            user_sub: user.profile?.sub,
            is_new_session: !window.performance || window.performance.navigation.type !== 1 // Non è un refresh
        });
    };

    const handleUserUnloaded = () => {
         console.log("Evento OIDC: User unloaded (logout)");
         setIsTokenExpired(false);
         setThreadId(null);
         pendingNewThreadId.current = null; // Pulisci anche il pending
         setMessages([{ id: Date.now() + Math.random(), text: INITIAL_AI_MESSAGE, sender: 'ai', isLoading: false, isError: false }]);
         setShowSuggestions(true);
         currentAiMessageId.current = null;
         setIsChatLoading(false);
         setThreadHistoryList([]);
         setIsThreadHistoryPanelOpen(false);
         setHasRequestedInitialHistory(false);
         // --- ANALYTICS: Logout utente ---
         trackEvent('user_logged_out');
    };

    auth.events.addAccessTokenExpired(handleAccessTokenExpired);
    auth.events.addUserLoaded(handleUserLoaded);
    auth.events.addUserUnloaded(handleUserUnloaded);

    return () => {
        auth.events.removeAccessTokenExpired(handleAccessTokenExpired);
        auth.events.removeUserLoaded(handleUserLoaded);
        auth.events.removeUserUnloaded(handleUserUnloaded);
    };
  }, [auth.isAuthenticated, auth.user?.expired, auth.user?.profile, auth.events, isStartingNewChat, threadId]);


  // Effetto per chiusura manuale WebSocket se non più necessario
  useEffect(() => {
      if (!wsConnectionURL && getWebSocket()?.readyState === ReadyState.OPEN) {
          console.log("FRONTEND: Chiusura manuale WebSocket (URL diventato null).");
          getWebSocket()?.close();
      }
  }, [wsConnectionURL, getWebSocket]);


  // Logout Cognito
  const signOutRedirect = () => {
    cleanupOidcStorage();
    const clientId = "5jepihvgjtg4jcou8vepf88ahb"; // Sostituisci con il tuo Client ID effettivo
    const logoutUri = "https://assistant.muvbcorp.com/"; // Sostituisci con la tua Logout URI effettiva
    const cognitoDomain = "https://auth.muvbcorp.com/"; // Sostituisci con il tuo Cognito Domain effettivo
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
    // --- ANALYTICS: Logout esplicito ---
    trackEvent('user_logout_redirect', { user_id: auth.user?.profile?.sub });
  };


  // Funzione per richiedere la lista dei thread (cronologia generale)
  const requestThreadHistoryList = useCallback(() => {
    if (auth.isAuthenticated && auth.user?.access_token && !isTokenExpired && readyState === ReadyState.OPEN && sendMessage) {
        console.log("FRONTEND: Richiesta della cronologia dei thread (getThreadHistory)...");
        setIsLoadingThreadHistoryList(true);
        sendMessage(JSON.stringify({
            action: 'getThreadHistory',
            token: auth.user.access_token
        }));
        // --- ANALYTICS: Richiesta cronologia thread ---
        trackEvent('chat_history_requested', { user_id: auth.user?.profile?.sub });
    } else {
        console.warn("Impossibile richiedere la cronologia dei thread: condizioni non soddisfatte.", {
            isAuthenticated: auth.isAuthenticated,
            hasToken: !!auth.user?.access_token,
            isTokenExpired,
            readyState,
            sendMessageExists: !!sendMessage
        });
        // --- ANALYTICS: Fallimento richiesta cronologia thread ---
        trackEvent('chat_history_request_failed', {
            user_id: auth.user?.profile?.sub,
            reason: 'conditions_not_met'
        });
    }
  }, [auth.isAuthenticated, auth.user?.access_token, isTokenExpired, readyState, sendMessage, auth.user?.profile?.sub]);


  // Gestione messaggi WebSocket in arrivo
  useEffect(() => {
    if (lastMessage !== null && lastMessage.data) {
      console.log("FRONTEND: Raw 'lastMessage.data' ricevuto:", lastMessage.data);
      try {
        const data = JSON.parse(lastMessage.data);
        console.log("FRONTEND: Messaggio (parsato) ricevuto da WS:", data);

        if (data && typeof data.type === 'string') {
          if (isStartingNewChat && ['threadIdUpdate', 'info', 'delta', 'end', 'error', 'authStatus', 'threadMessages'].includes(data.type)) {
              setIsStartingNewChat(false);
          }
          if (isChatLoading && ['threadMessages', 'info', 'threadIdUpdate', 'authStatus', 'error'].includes(data.type)) {
              setIsChatLoading(false);
          }

          switch (data.type) {
            case 'delta':
              setShowSuggestions(false);
              setMessages(prevMessages => {
                const currentAiMsg = prevMessages.find(msg => msg.id === currentAiMessageId.current && msg.sender === 'ai');
                if (currentAiMsg) {
                    const newText = (currentAiMsg.text === TIMEOUT_RECOVERY_TEXT || currentAiMsg.text === NEW_CHAT_AI_MESSAGE || currentAiMsg.text === INITIAL_AI_MESSAGE || currentAiMsg.text === '' ? '' : currentAiMsg.text) + data.payload;
                    lastAiMessageText.current = newText; // Accumula il testo della risposta AI
                    return prevMessages.map(msg =>
                        msg.id === currentAiMessageId.current
                        ? { ...msg, text: newText, isLoading: true, isError: false }
                        : msg
                    );
                } else { 
                    const newIdForDelta = currentAiMessageId.current || (Date.now() + Math.random());
                    if (!currentAiMessageId.current) currentAiMessageId.current = newIdForDelta;
                    lastAiMessageText.current = data.payload; // Inizia ad accumulare
                    console.warn(`FRONTEND DELTA: Nessun messaggio AI trovato per ID ${currentAiMessageId.current}. Aggiungo nuovo con ID ${newIdForDelta}.`);
                    return [...prevMessages, { id: newIdForDelta, text: data.payload, sender: 'ai', isLoading: true, isError: false }];
                }
              });
              break;

            case 'status':
              if (data.payload === 'typing') {
                setShowSuggestions(false);
                setMessages(prevMessages => {
                  if (!currentAiMessageId.current) { 
                      currentAiMessageId.current = Date.now() + Math.random();
                  }
                  const existingMsgIndex = prevMessages.findIndex(m => m.id === currentAiMessageId.current && m.sender === 'ai');
                  if (existingMsgIndex === -1) {
                      return [...prevMessages, { id: currentAiMessageId.current, text: '', sender: 'ai', isLoading: true, isError: false }];
                  } else {
                      return prevMessages.map((msg, index) =>
                          index === existingMsgIndex
                              ? { ...msg, isLoading: true, isError: false, text: (msg.text === TIMEOUT_RECOVERY_TEXT || msg.text === NEW_CHAT_AI_MESSAGE || msg.text === INITIAL_AI_MESSAGE) ? '' : msg.text }
                              : msg
                      );
                  }
                });
              }
              break;

            case 'end':
              console.log("FRONTEND END: Ricevuto. currentAiMessageId:", currentAiMessageId.current);
              let messageFoundAndStopped = false;
              setMessages(prevMessages => {
                const updatedMessages = prevMessages.map(msg => {
                  if (msg.id === currentAiMessageId.current && msg.sender === 'ai' && msg.isLoading) {
                    console.log(`FRONTEND END: Trovato messaggio AI attivo con ID ${currentAiMessageId.current}. Imposto isLoading: false.`);
                    messageFoundAndStopped = true;
                    return { ...msg, isLoading: false };
                  }
                  return msg;
                });
                if (!messageFoundAndStopped) {
                  console.warn(`FRONTEND END: Nessun messaggio AI *attivo* trovato con ID ${currentAiMessageId.current}. Tentativo di fallback...`);
                  let lastAiLoadingIndex = -1;
                  for (let i = updatedMessages.length - 1; i >= 0; i--) {
                    if (updatedMessages[i].sender === 'ai' && updatedMessages[i].isLoading) {
                      lastAiLoadingIndex = i;
                      break;
                    }
                  }
                  if (lastAiLoadingIndex !== -1) {
                    console.log(`FRONTEND END (Fallback): Trovato ultimo messaggio AI isLoading all'indice ${lastAiLoadingIndex}. Imposto isLoading: false.`);
                    updatedMessages[lastAiLoadingIndex] = { ...updatedMessages[lastAiLoadingIndex], isLoading: false };
                    lastAiMessageText.current = updatedMessages[lastAiLoadingIndex].text; // Cattura il testo finale
                  } else {
                    console.warn("FRONTEND END (Fallback): Nessun messaggio AI isLoading trovato.");
                  }
                }
                // --- ANALYTICS: Risposta AI completata ---
                if (userMessageTimestamp.current && lastUserMessageId.current) {
                    const latency = Date.now() - userMessageTimestamp.current;
                    trackEvent('chat_ai_response_completed', {
                        user_id: auth.user?.profile?.sub,
                        thread_id: threadId || pendingNewThreadId.current,
                        user_message_id: lastUserMessageId.current,
                        user_message_text: lastUserMessageText.current, // Attenzione PII!
                        ai_response_text: lastAiMessageText.current,   // Attenzione PII!
                        response_latency_ms: latency,
                        message_length: (lastAiMessageText.current || '').length,
                        is_error: false
                    });
                    userMessageTimestamp.current = null; // Resetta per il prossimo messaggio
                    lastUserMessageId.current = null;
                    lastUserMessageText.current = null;
                    lastAiMessageText.current = null;
                }
                return updatedMessages;
              });
              console.log("FRONTEND END: Resetto currentAiMessageId.current a null.");
              currentAiMessageId.current = null;
              inputRef.current?.focus();
              break;

            case 'threadMessages': 
                console.log("FRONTEND: Ricevuto 'threadMessages'", data.payload);
                setIsChatLoading(false);
                
                if (data.payload && data.payload.messages && data.payload.threadId) {
                    const historicalMessages = data.payload.messages.map(msg => ({
                        id: msg.id || Date.now() + Math.random(), 
                        text: msg.text.replace(SQUARE_BRACKET_CITATION_REGEX, ''),
                        sender: msg.sender,
                        isLoading: false,
                        isError: false,
                    }));

                    if (historicalMessages.length === 0 && data.payload.threadId) { 
                        setMessages([{ id: Date.now() + Math.random(), text: INITIAL_AI_MESSAGE, sender: 'ai', isLoading: false, isError: false }]);
                    } else if (historicalMessages.length > 0) {
                        setMessages(historicalMessages);
                    } else { 
                         setMessages([{ id: Date.now() + Math.random(), text: INITIAL_AI_MESSAGE, sender: 'ai', isLoading: false, isError: false }]);
                    }
                    setThreadId(data.payload.threadId); 
                    setShowSuggestions(historicalMessages.length === 0);
                    currentAiMessageId.current = null;

                    // --- ANALYTICS: Thread caricato dalla cronologia ---
                    trackEvent('chat_thread_loaded', {
                        user_id: auth.user?.profile?.sub,
                        thread_id: data.payload.threadId, // Corretto da data.payload.thread_id a data.payload.threadId
                        message_count: historicalMessages.length,
                        source: 'history_selection' // O 'initial_load'
                    });

                } else { 
                    setMessages([{ id: Date.now() + Math.random(), text: INITIAL_AI_MESSAGE, sender: 'ai', isLoading: false, isError: false }]);
                    setThreadId(data.payload?.threadId || null); 
                    setShowSuggestions(true);
                    // Rimosso: chat_thread_loaded_empty_or_failed
                }
                if (!hasRequestedInitialHistory && auth.isAuthenticated) {
                    requestThreadHistoryList();
                    setHasRequestedInitialHistory(true);
                }
                break;

            case 'threadHistoryList': 
                console.log("FRONTEND: Ricevuto 'threadHistoryList'", data.payload);
                if (Array.isArray(data.payload)) {
                    const sortedThreads = data.payload.sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
                    setThreadHistoryList(sortedThreads);
                    // --- ANALYTICS: Cronologia thread ricevuta ---
                    trackEvent('chat_history_received', {
                        user_id: auth.user?.profile?.sub,
                        thread_count: data.payload.length
                    });
                } else {
                    console.warn("FRONTEND: 'threadHistoryList' payload non è un array:", data.payload);
                    setThreadHistoryList([]);
                    // --- ANALYTICS: Errore cronologia thread ---
                    trackEvent('chat_history_error', {
                        user_id: auth.user?.profile?.sub,
                        reason: 'payload_not_array'
                    });
                }
                setIsLoadingThreadHistoryList(false);
                break;

            case 'threadIdUpdate': 
              console.log("FRONTEND THREAD_ID_UPDATE: Ricevuto nuovo ID:", data.payload, "Valore corrente di pendingNewThreadId.current PRIMA dell'aggiornamento:", pendingNewThreadId.current, "Stato threadId PRIMA:", threadId);
              setThreadId(data.payload);
              pendingNewThreadId.current = data.payload; // MODIFICA: Memorizza anche nel ref
              console.log("FRONTEND THREAD_ID_UPDATE: setThreadId chiamato con:", data.payload, ". pendingNewThreadId.current ORA è:", pendingNewThreadId.current);
              if (isStartingNewChat) setIsStartingNewChat(false);
              if (isChatLoading) setIsChatLoading(false);
              // --- ANALYTICS: ID del thread aggiornato ---
              trackEvent('chat_thread_id_updated', {
                  user_id: auth.user?.profile?.sub,
                  new_thread_id: data.payload,
                  source: isStartingNewChat ? 'new_chat_request' : 'backend_init'
              });
              break;

            case 'authStatus': 
                console.log("FRONTEND: Ricevuto 'authStatus'", data.payload);
                setIsChatLoading(false);
                if (data.payload?.authenticated && !hasRequestedInitialHistory && auth.isAuthenticated) {
                    requestThreadHistoryList();
                    setHasRequestedInitialHistory(true);
                } else if (!data.payload?.authenticated) {
                     console.warn("AuthStatus dal backend WS indica non autenticato:", data.payload?.message);
                     // Rimosso: auth_status_unauthenticated_backend
                }
                break;

            case 'info': 
              console.log("FRONTEND: Ricevuto 'info'", data.payload);
              const isAuthRelatedInfo = data.payload?.toLowerCase().includes("autenticazione") || data.payload?.toLowerCase().includes("sessione");
              if (isAuthRelatedInfo && !hasRequestedInitialHistory && auth.isAuthenticated) {
                  requestThreadHistoryList(); 
                  setHasRequestedInitialHistory(true);
              }
              if (isAuthRelatedInfo || data.payload === "Nuova conversazione avviata." || data.payload?.toLowerCase().includes("sessione chat precedente non trovata")) {
                  setIsChatLoading(false); 
              }
              // Rimosso: chat_info_message
              break;

            case 'error': 
              console.error("FRONTEND: Ricevuto 'error' strutturato dal backend:", data.payload);
              setIsStartingNewChat(false);
              setIsChatLoading(false);
              setIsLoadingThreadHistoryList(false);
              const errorPayloadString = typeof data.payload === 'string' ? data.payload.toLowerCase() : '';
              if (errorPayloadString.includes('token scaduto') || errorPayloadString.includes('unauthorized') || errorPayloadString.includes('autenticazione fallita') || errorPayloadString.includes('token mancante') || errorPayloadString.includes('token non valido')) {
                  setIsTokenExpired(true);
                  // --- ANALYTICS: Errore: token scaduto/non valido dal backend ---
                  trackEvent('auth_token_error_backend', {
                      user_id: auth.user?.profile?.sub,
                      error_type: 'token_issue',
                      error_message: data.payload
                  });
              } else {
                  // --- ANALYTICS: Errore generico dal backend ---
                  trackEvent('chat_backend_error', {
                      user_id: auth.user?.profile?.sub,
                      thread_id: threadId || pendingNewThreadId.current,
                      error_message: data.payload, // Attenzione PII!
                      user_message_id: lastUserMessageId.current, // Associa all'ultimo messaggio utente
                      user_message_text: lastUserMessageText.current, // Attenzione PII!
                      ai_response_text: lastAiMessageText.current, // Attenzione PII! (se errore arriva dopo delta)
                      is_token_expired: isTokenExpired // Stato corrente per contesto
                  });
                  // Resetta lo stato di tracciamento latenza/messaggio se c'è un errore
                  userMessageTimestamp.current = null;
                  lastUserMessageId.current = null;
                  lastUserMessageText.current = null;
                  lastAiMessageText.current = null;
              }

              setMessages(prevMessages => {
                  const updatedOnErrorMessages = prevMessages.map(msg =>
                      (msg.id === currentAiMessageId.current && msg.sender === 'ai')
                          ? { ...msg, isLoading: false, isError: true, text: msg.text || "(Risposta interrotta)" }
                          : msg
                  );
                  const errorAlreadyShown = updatedOnErrorMessages.some(m => m.isError && m.text?.includes(String(data.payload)));
                  if (!isTokenExpired && !errorAlreadyShown) {
                    return [ ...updatedOnErrorMessages, { id: Date.now() + Math.random(), text: `Errore: ${data.payload}`, sender: 'ai', isError: true, isLoading: false }];
                  }
                  return updatedOnErrorMessages;
              });
              currentAiMessageId.current = null;
              inputRef.current?.focus();
              break;

            default: 
              console.warn("FRONTEND: Tipo messaggio WS sconosciuto:", data.type, data);
              setIsStartingNewChat(false); setIsChatLoading(false); setIsLoadingThreadHistoryList(false);
              setMessages(prev => [...prev, { id: Date.now(), text: "Errore: Risposta non valida dal server.", sender: 'ai', isError: true, isLoading: false }]);
              currentAiMessageId.current = null;
              // Rimosso: websocket_unknown_message_type
          }
        } else if (data && data.message && typeof data.message === 'string' && data.message.toLowerCase().includes("endpoint request timed out")) { 
            console.error("FRONTEND: Ricevuto TIMEOUT da API Gateway:", data.message);
            setIsStartingNewChat(false); setIsChatLoading(false); setIsLoadingThreadHistoryList(false);
            setMessages(prevMessages => prevMessages.map(msg => msg.id === currentAiMessageId.current && msg.sender === 'ai' ? { ...msg, isLoading: false, isError: true, text: msg.text || TIMEOUT_RECOVERY_TEXT } : msg ));
            currentAiMessageId.current = null;
            // --- ANALYTICS: Timeout API Gateway ---
            trackEvent('api_gateway_timeout', {
                user_id: auth.user?.profile?.sub,
                thread_id: threadId || pendingNewThreadId.current,
                user_message_id: lastUserMessageId.current,
                user_message_text: lastUserMessageText.current // Attenzione PII!
            });
            // Resetta lo stato di tracciamento latenza/messaggio se c'è un timeout
            userMessageTimestamp.current = null;
            lastUserMessageId.current = null;
            lastUserMessageText.current = null;
            lastAiMessageText.current = null;
        } else { 
          console.warn("FRONTEND: Messaggio WS non strutturato o sconosciuto ricevuto:", data);
          setIsStartingNewChat(false); setIsChatLoading(false); setIsLoadingThreadHistoryList(false);
          setMessages(prev => [...prev, { id: Date.now(), text: "Errore: Risposta non valida dal server.", sender: 'ai', isError: true, isLoading: false }]);
          currentAiMessageId.current = null;
          // Rimosso: websocket_malformed_message
        }
      } catch (error) { 
        console.error("FRONTEND: Errore parsing/gestione messaggio WS:", error, "Dati:", lastMessage.data);
        setIsStartingNewChat(false); setIsChatLoading(false); setIsLoadingThreadHistoryList(false);
        setMessages(prev => [...prev, { id: Date.now(), text: "Errore: Problema di comunicazione.", sender: 'ai', isError: true, isLoading: false }]);
        currentAiMessageId.current = null;
        // Rimosso: websocket_parsing_error
        // Resetta lo stato di tracciamento latenza/messaggio se c'è un errore
        userMessageTimestamp.current = null;
        lastUserMessageId.current = null;
        lastUserMessageText.current = null;
        lastAiMessageText.current = null;
      }
    }
  }, [
    lastMessage,
    auth.isAuthenticated, 
    isStartingNewChat,
    isChatLoading,
    hasRequestedInitialHistory,
    requestThreadHistoryList, 
    isTokenExpired,
    threadId,
    auth.user?.profile?.sub
  ]);


  // Invio messaggio utente al backend
  const handleSendMessage = useCallback(async (text) => {
    const trimmedText = text.trim();
    if (!trimmedText || isTokenExpired || isStartingNewChat || isChatLoading || readyState !== ReadyState.OPEN) {
        console.warn("HANDLE_SEND_MESSAGE: Invio messaggio bloccato:", { trimmedText, isTokenExpired, isStartingNewChat, isChatLoading, readyState });
        // --- ANALYTICS: Tentativo invio messaggio bloccato ---
        trackEvent('chat_message_send_blocked', {
            user_id: auth.user?.profile?.sub,
            reason: isTokenExpired ? 'token_expired' : isStartingNewChat ? 'starting_new_chat' : isChatLoading ? 'chat_loading' : readyState !== ReadyState.OPEN ? 'websocket_not_open' : 'empty_message'
        });
        return;
    }
    setShowSuggestions(false);

    const isFirstMessageForNewThreadFlag = newThreadJustCreatedAndMessaged.current;

    if (auth.isAuthenticated && auth.user?.access_token && sendMessage) {
      const userMessage = { id: Date.now() + Math.random(), text: trimmedText, sender: 'user', isLoading: false, isError: false };
      const newAiResponsePlaceholderId = Date.now() + Math.random(); 
      currentAiMessageId.current = newAiResponsePlaceholderId; 

      setMessages((prevMessages) => [ ...prevMessages, userMessage, { id: newAiResponsePlaceholderId, text: '', sender: 'ai', isLoading: true, isError: false } ]);

      // Prepara i ref per la latenza e il logging della risposta AI
      userMessageTimestamp.current = Date.now();
      lastUserMessageId.current = userMessage.id;
      lastUserMessageText.current = trimmedText; // Salva il testo della domanda (Attenzione PII!)
      lastAiMessageText.current = null; // Resetta per la nuova risposta

      const idForThisMessage = pendingNewThreadId.current || threadId;
      console.log("HANDLE_SEND_MESSAGE: Invio con threadId:", idForThisMessage, "(pendingRef:", pendingNewThreadId.current, "state-threadId:", threadId, ")");

      const messagePayload = {
          action: 'sendMessage',
          message: trimmedText,
          threadId: idForThisMessage, // Usa l'ID determinato
          token: auth.user.access_token,
          userAttributes: { email: auth.user?.profile?.email, sub: auth.user?.profile?.sub }
      };
      sendMessage(JSON.stringify(messagePayload));

      // --- ANALYTICS: Messaggio utente inviato ---
      trackEvent('chat_message_sent', {
          user_id: auth.user?.profile?.sub,
          thread_id: idForThisMessage,
          message_id: userMessage.id,
          message_text: trimmedText, // ATTENZIONE: Questo logga il testo della domanda. Considera la PII!
          message_length: trimmedText.length,
          source: 'manual_input' // O 'suggestion_click' se provenisse da handleSuggestionClick
      });

      if (pendingNewThreadId.current && idForThisMessage === pendingNewThreadId.current) {
          console.log("HANDLE_SEND_MESSAGE: pendingNewThreadId.current (" + pendingNewThreadId.current + ") usato e pulito.");
          pendingNewThreadId.current = null;
          newThreadJustCreatedAndMessaged.current = false;
      } else if (isFirstMessageForNewThreadFlag && !pendingNewThreadId.current) {
          newThreadJustCreatedAndMessaged.current = false;
      }

      if (isFirstMessageForNewThreadFlag) {
        console.log("HANDLE_SEND_MESSAGE: Primo messaggio inviato a un nuovo thread (flag era true). Richiedo aggiornamento cronologia tra poco.");
        setTimeout(() => {
            requestThreadHistoryList();
        }, 2500); 
      }
    } else {
      const errorReason = isTokenExpired ? "Sessione scaduta." : isStartingNewChat ? "Avvio chat..." : isChatLoading ? "Caricamento chat..." : !sendMessage ? "Servizio chat non pronto." : "Non connesso.";
      setMessages(prev => [...prev, { id: Date.now(), text: `Errore: ${errorReason} Impossibile inviare.`, sender: 'ai', isError: true, isLoading: false }]);
      // --- ANALYTICS: Fallimento invio messaggio (frontend) ---
      trackEvent('chat_message_send_failed_frontend', {
          user_id: auth.user?.profile?.sub,
          reason: errorReason
      });
    }
  }, [
      readyState,
      sendMessage,
      threadId,
      auth.isAuthenticated,
      auth.user?.access_token,
      auth.user?.profile,
      isTokenExpired,
      isStartingNewChat,
      isChatLoading,
      requestThreadHistoryList
  ]);

  // Click su un suggerimento predefinito
  const handleSuggestionClick = (suggestionText) => {
    handleSendMessage(suggestionText); 
    // --- ANALYTICS: Suggerimento cliccato ---
    trackEvent('chat_suggestion_clicked', {
        user_id: auth.user?.profile?.sub,
        suggestion_text: suggestionText // ATTENZIONE: Questo logga il testo del suggerimento. PII?
    });
  };

  // Avvio di una nuova conversazione (nuovo thread)
  const handleStartNewConversation = useCallback(() => {
    const localIsAiResponding = messages.some(msg => msg.isLoading && msg.sender === 'ai');
    if (isStartingNewChat || isChatLoading || (auth.isAuthenticated && localIsAiResponding) || readyState !== ReadyState.OPEN || !sendMessage) {
        console.warn("START_NEW_CONV: Avvio nuova conversazione bloccato:", {isStartingNewChat, isChatLoading, localIsAiResponding, readyState, sendMessageExists: !!sendMessage});
        // --- ANALYTICS: Tentativo nuova chat bloccato ---
        trackEvent('chat_new_conversation_blocked', {
            user_id: auth.user?.profile?.sub,
            reason: isStartingNewChat ? 'already_starting' : isChatLoading ? 'chat_loading' : localIsAiResponding ? 'ai_responding' : readyState !== ReadyState.OPEN ? 'websocket_not_open' : 'unknown'
        });
        return;
    }
    if (isTokenExpired && auth.isAuthenticated) {
        // --- ANALYTICS: Tentativo nuova chat bloccato (token scaduto) ---
        trackEvent('chat_new_conversation_blocked', {
            user_id: auth.user?.profile?.sub,
            reason: 'token_expired'
        });
        return; 
    }

    console.log("START_NEW_CONV: Richiesta di avvio nuova conversazione. threadId attuale (stato):", threadId, "pendingNewThreadId.current attuale:", pendingNewThreadId.current);
    setIsStartingNewChat(true);
    setIsChatLoading(true); 

    const newChatWelcomeAiId = Date.now() + Math.random();
    currentAiMessageId.current = null; 

    setMessages([{ id: newChatWelcomeAiId, text: NEW_CHAT_AI_MESSAGE, sender: 'ai', isLoading: false, isError: false }]);
    setShowSuggestions(true);
    
    pendingNewThreadId.current = null;
    setThreadId(null); 
    console.log("START_NEW_CONV: pendingNewThreadId.current resettato a null. setThreadId(null) chiamato.");

    const payload = { action: 'startNewThread', token: auth.user?.access_token };
    sendMessage(JSON.stringify(payload));

    newThreadJustCreatedAndMessaged.current = true; 
    if(isThreadHistoryPanelOpen) setIsThreadHistoryPanelOpen(false); 

    // --- ANALYTICS: Nuova conversazione avviata ---
    trackEvent('chat_new_conversation_started', {
        user_id: auth.user?.profile?.sub,
        source: 'new_chat_button_click'
    });
  }, [
      readyState,
      sendMessage,
      auth.isAuthenticated,
      auth.user?.access_token,
      isTokenExpired,
      isStartingNewChat,
      isChatLoading,
      messages, 
      isThreadHistoryPanelOpen,
      threadId,
      auth.user?.profile?.sub
  ]);


  // Selezione di un thread dalla cronologia del pannello
  const handleSelectThreadFromHistory = useCallback((selectedThreadId) => {
    if (!selectedThreadId || selectedThreadId === threadId || isChatLoading) {
        if (isThreadHistoryPanelOpen) setIsThreadHistoryPanelOpen(false);
        // --- ANALYTICS: Tentativo selezione thread bloccato ---
        trackEvent('chat_select_thread_blocked', {
            user_id: auth.user?.profile?.sub,
            selected_thread_id: selectedThreadId,
            reason: selectedThreadId === threadId ? 'already_active' : isChatLoading ? 'chat_loading' : 'invalid_id_or_closed_panel'
        });
        return;
    }
    console.log(`FRONTEND: Selezione thread dalla cronologia: ${selectedThreadId}. threadId attuale (stato): ${threadId}`);
    setMessages([]); 
    setIsChatLoading(true); 
    
    pendingNewThreadId.current = null;
    setThreadId(selectedThreadId); 
    console.log(`SELECT_THREAD_HISTORY: pendingNewThreadId.current resettato a null. setThreadId chiamato con: ${selectedThreadId}`);

    setShowSuggestions(false); 
    currentAiMessageId.current = null; 

    if (auth.isAuthenticated && auth.user?.access_token && readyState === ReadyState.OPEN && sendMessage) {
        const authPayload = {
            action: 'authenticate',
            token: auth.user.access_token,
            userInfo: {
                email: auth.user?.profile?.email,
                sub: auth.user?.profile?.sub,
                threadId: selectedThreadId 
            }
        };
        sendMessage(JSON.stringify(authPayload));
        // --- ANALYTICS: Thread selezionato con successo ---
        trackEvent('chat_thread_selected_from_history', {
            user_id: auth.user?.profile?.sub,
            selected_thread_id: selectedThreadId
        });
    } else {
        console.error("Impossibile selezionare thread: utente non autenticato o WebSocket non pronto.");
        setIsChatLoading(false);
        setMessages([{id: Date.now(), text: "Errore nel caricare la conversazione selezionata.", sender: 'ai', isError: true, isLoading: false}]);
        // --- ANALYTICS: Fallimento selezione thread (frontend) ---
        trackEvent('chat_select_thread_failed_frontend', {
            user_id: auth.user?.profile?.sub,
            selected_thread_id: selectedThreadId,
            reason: auth.isAuthenticated ? 'websocket_not_ready' : 'not_authenticated'
        });
    }
    if (isThreadHistoryPanelOpen) setIsThreadHistoryPanelOpen(false); 
  }, [
      auth.isAuthenticated,
      auth.user?.access_token,
      auth.user?.profile,
      readyState,
      sendMessage,
      threadId, 
      isChatLoading,
      isThreadHistoryPanelOpen 
  ]);

  // Determina se l'AI sta attualmente rispondendo
  const isAiCurrentlyResponding = messages.some(msg => msg.isLoading && msg.sender === 'ai');

  // --- Componenti UI di Fallback e Loading ---
  const TokenExpiredModal = () => (
    <div className="modal-overlay">
      <div className="modal-content auth-container error-message" style={{flexDirection: 'column'}}>
        <h3>Sessione Scaduta</h3>
        <p>La tua sessione di accesso è scaduta. Per continuare, effettua nuovamente l'accesso.</p>
        <div className="error-actions"> 
          <button onClick={() => {setIsTokenExpired(false); auth.signinRedirect();}} className="auth-button">Accedi di nuovo</button>
          <button onClick={signOutRedirect} className="auth-button secondary">Esci</button>
        </div>
      </div>
    </div>
  );

  const AuthErrorFallback = ({ error }) => (
    <div className="chat-app"> 
      <div className="full-page-overlay"> {/* Usa la nuova classe per l'overlay a tutta pagina */}
        <div className="error-message"> {/* Questo è il blocco del messaggio centrato */}
          <h3>Problema di autenticazione</h3>
          <p>{error?.message || "Si è verificato un errore durante l'autenticazione."}</p>
          <p>Questo può accadere se la pagina è stata ricaricata durante il login o se c'è un problema con i dati salvati.</p>
          <div className="error-actions">
            <button onClick={() => { cleanupOidcStorage(); auth.signinRedirect(); }} className="auth-button">Riprova autenticazione</button>
            <button onClick={() => { cleanupOidcStorage(); window.location.href = window.location.origin; }} className="auth-button secondary">Torna alla home</button>
          </div>
        </div>
      </div>
    </div>
  );

  // --- Render Logic ---
  if (auth.isLoading) {
    return (
        <div className="chat-app">
            <div className="full-page-overlay"> {/* Usa la nuova classe per l'overlay a tutta pagina */}
                <div className="loading-message"> {/* Questo è il blocco del messaggio centrato */}
                    <div className="spinner big-spinner"></div>
                    <span>Caricamento autenticazione...</span>
                </div>
            </div>
        </div>
    );
  }

  if (auth.error) {
     if (auth.error.message === "No matching state found in storage" || auth.error.name === "ErrorTimeout" || auth.error.message.includes("state") || auth.error.message.includes("nonce")) {
       return <AuthErrorFallback error={auth.error} />;
     }
     return (
        <div className="chat-app">
            <div className="full-page-overlay"> {/* Usa la nuova classe per l'overlay a tutta pagina */}
                <div className="error-message"> {/* Questo è il blocco del messaggio centrato */}
                    <h3>Errore Inaspettato</h3>
                    <p>Si è verificato un errore durante il processo di autenticazione:</p>
                    <pre style={{whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>{auth.error.message || auth.error.toString()}</pre>
                    <button onClick={() => window.location.reload()} className="auth-button">Ricarica Pagina</button>
                </div>
            </div>
        </div>
    );
  }

  if (!auth.isAuthenticated) {
    return (
        <div className="landing-page">
            {/* Background Elements */}
            <div id="circles"></div>
            <div id="sparkling"></div>
            <div id="sparkling_light"></div>
            
            {/* Skip Link */}
            <a className="skip-link screen-reader-text" href="#primary">Skip to content</a>
            
            {/* Site Header */}
            <div className="container text-center">
                <div className="row">
                    <header id="masthead" className="landing-site-header">
                        <div className="landing-site-branding">
                            <a href="#" rel="home">
                                <img src="/mascotte_ai.png" alt="MUV Assistant" />
                            </a>
                            <h3>Rivoluziona la Mobilità Sostenibile della tua Azienda</h3>
                            <h1>MUV Assistant</h1>
                            <p className="site-description">Insight e Dati sulla Mobilità Sostenibile potenziati da OpenAI.</p>
                            <p>Lo strumento per mobility managers di aziende, scuole, università e organizzazioni pubbliche</p>
                            <p>
                                <button 
                                    onClick={() => auth.signinRedirect()} 
                                    className="a__button button_primary"
                                >
                                    Provalo subito
                                </button>
                            </p>
                        </div>
                    </header>
                </div>
            </div>
            
            {/* Main Content */}
            <main id="primary" className="site-main">
                <div className="landing-main-content">
                    <div className="landing-row">
                        <div className="landing-col-md-6">
                            <div className="lightbox roundedcorner">
                                <div className="muvopenai">
                                    <img src="/muvopenai.png" className="img-fluid" alt="muv assistant engine" />
                                </div>
                                <h3>Guida il cambiamento verso una Mobilità Sostenibile</h3>
                                <section className="lightboxcontent">
                                    <p>Semplifica la tua esperienza nella creazione e gestione di piani di mobilità sostenibile. MUV Assistant ti supporta concretamente verso soluzioni efficaci e conformi alle normative vigenti.</p>
                                    <hr />
                                    <p>Dedicato ai mobility managers di aziende, scuole, università e organizzazioni pubbliche, che desiderano integrare pratiche innovative nella gestione della mobilità sostenibile.</p>
                                    <hr />
                                    <p>Con MUV Assistant esplori il metodo più efficiente per sviluppare e implementare piani di mobilità sostenibile. Ricevi istruzioni dettagliate sui passaggi da seguire e sui documenti necessari per creare i tuoi PSCL.</p>
                                </section>
                            </div>
                        </div>
                        <div className="landing-col-md-6">
                            <div className="accentbox roundedcorner">
                                <h3>Un Alleato per i tuoi Piani di Mobilità</h3>
                                
                                <section className="scrolltxt">
                                    <div className="stage_animation">
                                        <div className="scrolldiv">Mobility</div>
                                        <div className="scrolldiv">PSCL</div>
                                        <div className="scrolldiv">TPL</div>
                                        <div className="scrolldiv">Mobility manager</div>
                                        <div className="scrolldiv">Mobility</div>
                                        <div className="scrolldiv">PSCL</div>
                                        <div className="scrolldiv">TPL</div>
                                        <div className="scrolldiv">Mobility manager</div>
                                        <div className="scrolldiv">Mobility</div>
                                        <div className="scrolldiv">PSCL</div>
                                        <div className="scrolldiv">TPL</div>
                                        <div className="scrolldiv">Mobility manager</div>
                                        <div className="scrolldiv">Mobility</div>
                                        <div className="scrolldiv">PSCL</div>
                                        <div className="scrolldiv">TPL</div>
                                        <div className="scrolldiv">Mobility manager</div>
                                        <div className="scrolldiv">Mobility</div>
                                        <div className="scrolldiv">PSCL</div>
                                        <div className="scrolldiv">TPL</div>
                                        <div className="scrolldiv">Mobility manager</div>
                                        <div className="scrolldiv">Mobility</div>
                                        <div className="scrolldiv">PSCL</div>
                                        <div className="scrolldiv">TPL</div>
                                        <div className="scrolldiv">Mobility manager</div>
                                        <div className="scrolldiv">Mobility</div>
                                        <div className="scrolldiv">PSCL</div>
                                        <div className="scrolldiv">TPL</div>
                                        <div className="scrolldiv">Mobility manager</div>
                                        <div className="scrolldiv">Mobility</div>
                                        <div className="scrolldiv">PSCL</div>
                                        <div className="scrolldiv">TPL</div>
                                        <div className="scrolldiv">Mobility manager</div>
                                    </div>
                                </section>
                                
                                <section className="infomobilitybox">
                                    <p className="text-center">Cosa posso chiedere a MUV Assistant?</p>
                                    <p className="infomobility">Devo sviluppare un piano per gli spostamenti casa-lavoro per i dipendenti della mia azienda. Cosa devo fare?</p>
                                    <p className="infomobility">Cos'è e come funziona il Piano Spostamenti Casa Lavoro.</p>
                                    <p className="infomobility">In che modo posso impiegare MUV per la mia azienda?</p>
                                    <p className="infomobility">Come posso incoraggiare i dipendenti a partecipare attivamente al Piano Spostamenti Casa-Lavoro?</p>
                                    <p className="infomobility">Quali strumenti tecnologici offre MUV per ottimizzare la mobilità aziendale?</p>
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="container">
                    <article id="post-11" className="hentry">
                        <div className="entry-content">
                        </div>
                    </article>
                </div>
            </main>
            
            {/* Footer */}
            <footer id="colophon" className="landing-footer">
                <div className="landing-site-branding text-center">
                    <a href="#" rel="home">
                        <img src="/mascotte_ai.png" alt="MUV Assistant" />
                    </a>
                    <h1>Prova <span className="accentspan">MUV Assistant</span> gratis</h1>
                    <p className="site-description">Insight e Dati sulla Mobilità Sostenibile potenziati da OpenAI</p>
                    <p>
                        <button 
                            onClick={() => auth.signinRedirect()} 
                            className="a__button button_primary"
                        >
                            Provalo subito
                        </button>
                    </p>
                    <p>
                        <a href="https://www.muvbcorp.com/" target="_blank" rel="noopener noreferrer">
                            Scopri MUV
                        </a>
                    </p>
                </div>
            </footer>
        </div>
    );
  }

  return (
    <div className="chat-app"> 
      {isTokenExpired && <TokenExpiredModal />}

      <ThreadHistoryPanel
        isOpen={isThreadHistoryPanelOpen}
        onClose={() => setIsThreadHistoryPanelOpen(false)}
        threads={threadHistoryList}
        activeThreadId={pendingNewThreadId.current || threadId} // Mostra il pending se c'è, altrimenti quello di stato
        onSelectThread={handleSelectThreadFromHistory}
        isLoading={isLoadingThreadHistoryList}
        onRefresh={requestThreadHistoryList}
        onStartNewConversation={handleStartNewConversation} // Passa la funzione per il bottone "Nuova Chat" nel pannello
      />

      <div className="main-content" style={{ marginLeft: isThreadHistoryPanelOpen ? '260px' : '0' }}> 
        <div className="header-container"> {/* Nuovo header per la schermata principale */}
          <div className="header-left">
            {/* ICONA HISTORY: Mostra solo se il pannello NON è aperto */}
            {!isThreadHistoryPanelOpen && (
              <button
                  onClick={() => setIsThreadHistoryPanelOpen(true)} // Apre il pannello
                  className="history-toggle-button" 
                  disabled={isTokenExpired || isLoadingThreadHistoryList || readyState !== ReadyState.OPEN}
                  title="Apri Cronologia Chat"
              >
                  <img src="/history_icon.svg" alt="Apri Cronologia Chat" />
              </button>
            )}
            {/* LOGO MUV ASSISTANT */}
            <img src="/muv_assistant_logo.svg" alt="MUV Assistant Logo" className="app-logo" /> 
            <span className="app-title">MUV ASSISTANT</span>
          </div>
          <div className="header-right">
            <span className="user-email-display">{auth.user?.profile?.username || "Utente"} - {auth.user?.profile?.email || "Email"}</span>
            <button
              onClick={() => window.open('https://calendly.com/muvbcorp/30-min', '_blank')} // Link Calendly
              className="auth-button sales-button"
            >
              Talk to Sales
            </button>
            <button
                onClick={signOutRedirect}
                className="auth-button logout-button"
                disabled={isTokenExpired}
            >
                <img
                    src="/logout_30dp_FFFFFF_FILL0_wght400_GRAD0_opsz24.svg" // Percorso da public folder
                    alt="Esci"
                />
            </button>
          </div>
        </div>

        {(isChatLoading && (!messages.length || messages[0].text === INITIAL_AI_MESSAGE || messages.every(m => m.sender === 'ai')) ) ? ( 
            <div className="chat-container">
                <div className="loading-chat-history">
                    <div className="spinner big-spinner"></div> 
                    <p>Caricamento conversazione...</p>
                </div>
            </div>
        ) : (
          <div className="chat-container">
              <MessageList messages={messages} /> 
              {showSuggestions && predefinedSuggestions.length > 0 && !isAiCurrentlyResponding && !isTokenExpired && !isStartingNewChat && !isChatLoading && (
              <div className="suggestion-area">
                  {predefinedSuggestions.map((suggestion, index) => (
                  <div key={index} className="suggestion-chip" onClick={() => handleSuggestionClick(suggestion)}>
                      {suggestion}
                  </div>
                  ))}
              </div>
              )}
              <MessageInput
                onSendMessage={handleSendMessage}
                isLoading={readyState !== ReadyState.OPEN || isAiCurrentlyResponding || isTokenExpired || isStartingNewChat || isChatLoading}
                inputRef={inputRef}
              />
              {readyState !== ReadyState.OPEN && readyState !== ReadyState.CONNECTING && !isTokenExpired &&
                <p className="connection-status-warning">
                    {readyState === ReadyState.CLOSING && "Disconnessione in corso..."}
                    {readyState === ReadyState.CLOSED && "Disconnesso. Tentativo di riconnessione..."}
                </p>
              }
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
